/**
 * Shared form-aware slot selection logic for resource- and service-backed appointments.
 * Split in two phases because the slot schedule hook sits between them: `useSlotSelectionState`
 * owns the inputs the schedule needs, `useSlotSelectionCore` reacts to the loaded schedule.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, type FormInstance } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type { SlotScheduleCoreResult } from './useSlotScheduleCore';
import { findFirstSlotStartOnDay } from './utils';
import { isAppointmentSlotSubmitDisabled } from './isAppointmentSlotSubmitDisabled';

/** Inputs for the selection state that must exist before the schedule hook runs. */
interface UseSlotSelectionStateProps {
  /** Form that owns the `startTime` field. */
  form: FormInstance;
  /** Appointment length in minutes; used to derive `endTime` from `startTime`. */
  duration: number;
  /** Whether `startTime` should be treated as already committed when the surface opens. */
  initialCommitted: boolean;
}

/**
 * Holds the visible month and commit flag, and derives `startTime`/`endTime` from the form.
 * @returns State consumed by the schedule hook and later by `useSlotSelectionCore`.
 */
export const useSlotSelectionState = ({
  form,
  duration,
  initialCommitted
}: UseSlotSelectionStateProps) => {
  const [slotsVisibleMonth, setSlotsVisibleMonth] = useState<Dayjs>(() => dayjs().startOf('month'));
  const [hasCommittedStartTime, setHasCommittedStartTime] = useState(initialCommitted);

  const startTime = Form.useWatch('startTime', form) as Dayjs | null | undefined;

  const endTime = useMemo(() => {
    if (startTime && dayjs.isDayjs(startTime) && startTime.isValid()) {
      return startTime.add(duration, 'minute');
    }
    return null;
  }, [startTime, duration]);

  return {
    startTime,
    endTime,
    slotsVisibleMonth,
    setSlotsVisibleMonth,
    hasCommittedStartTime,
    setHasCommittedStartTime
  };
};

/** State shape produced by `useSlotSelectionState`. */
export type SlotSelectionState = ReturnType<typeof useSlotSelectionState>;

/** Inputs for the shared selection orchestration. */
interface UseSlotSelectionCoreProps {
  /** Form that owns the `startTime` field. */
  form: FormInstance;
  /** Whether the hosting dialog or surface is open and should load data. */
  visible: boolean;
  /** Optional calendar day to steer the picker without an initial committed time. */
  preferredDate?: Dayjs;
  /** Appointment length in minutes; a change re-shows a dismissed unavailability alert. */
  duration: number;
  /**
   * Values whose change invalidates the current selection. The first key is the slot source
   * (resource or service id) and must be set for any slot interaction to happen.
   */
  identityKeys: readonly (string | null | undefined)[];
  /** Extra readiness gate for slot interactions (e.g. a required primary resource is chosen). */
  isSlotsQueryReady: boolean;
  /** Whether an identity change also clears the form's `startTime`. */
  clearStartTimeOnIdentityChange: boolean;
  /** Output of `useSlotSelectionState`. */
  state: SlotSelectionState;
  /** Output of the resource or service slot schedule hook. */
  schedule: SlotScheduleCoreResult & { truncated: boolean };
}

/**
 * Coordinates a loaded slot schedule with form state: autofills the first slot of a picked or
 * preferred day, resets on identity change, and exposes open/close lifecycle helpers.
 * @returns Picker handlers, alert inputs, lifecycle helpers, and submit guard flags.
 */
export const useSlotSelectionCore = ({
  form,
  visible,
  preferredDate,
  duration,
  identityKeys,
  isSlotsQueryReady,
  clearStartTimeOnIdentityChange,
  state,
  schedule
}: UseSlotSelectionCoreProps) => {
  const {
    startTime,
    endTime,
    slotsVisibleMonth,
    setSlotsVisibleMonth,
    hasCommittedStartTime,
    setHasCommittedStartTime
  } = state;
  const {
    slots,
    timezone,
    slotsByDateKey,
    isCheckingSlots,
    slotUnavailable,
    truncated,
    isCheckingSelectedMonthSlots
  } = schedule;
  const sourceId = identityKeys[0];

  const [dismissedSlotUnavailable, setDismissedSlotUnavailable] = useState(false);
  const [preferredPickerValue, setPreferredPickerValue] = useState<Dayjs | undefined>(undefined);
  const previousIdentityKeysRef = useRef<readonly (string | null | undefined)[] | undefined>(undefined);
  const dayPlusAutofillAppliedRef = useRef(false);
  const pendingCalendarDayRef = useRef<Dayjs | null>(null);
  const committedInitializationRef = useRef(false);

  const displayedSlotUnavailable = dismissedSlotUnavailable ? null : slotUnavailable;

  const applyFirstSlotForDay = useCallback((selectedDay: Dayjs) => {
    if (!selectedDay.isValid()) {
      return;
    }

    const firstSlot = findFirstSlotStartOnDay(selectedDay, slots, timezone, slotsByDateKey);
    if (!firstSlot) {
      form.setFieldValue('startTime', null);
      setHasCommittedStartTime(false);
      return;
    }

    form.setFieldValue('startTime', firstSlot);
    setHasCommittedStartTime(true);
  }, [form, slots, slotsByDateKey, timezone, setHasCommittedStartTime]);

  const handleSlotsVisibleMonthChange = useCallback((month: Dayjs) => {
    const nextMonth = month.startOf('month');
    setSlotsVisibleMonth((currentMonth) => (
      currentMonth.isSame(nextMonth, 'month') ? currentMonth : nextMonth
    ));
  }, [setSlotsVisibleMonth]);

  const handleCalendarDaySelect = useCallback((selectedDay: Dayjs) => {
    if (!visible || !sourceId || !isSlotsQueryReady) {
      return;
    }

    pendingCalendarDayRef.current = selectedDay.startOf('day');
    if (isCheckingSlots || !slots) {
      return;
    }

    queueMicrotask(() => {
      applyFirstSlotForDay(selectedDay);
      pendingCalendarDayRef.current = null;
    });
  }, [visible, sourceId, isSlotsQueryReady, isCheckingSlots, slots, applyFirstSlotForDay]);

  const resetForClose = useCallback(() => {
    setDismissedSlotUnavailable(false);
    setHasCommittedStartTime(false);
    setPreferredPickerValue(undefined);
    previousIdentityKeysRef.current = undefined;
    dayPlusAutofillAppliedRef.current = false;
    pendingCalendarDayRef.current = null;
    committedInitializationRef.current = false;
  }, [setHasCommittedStartTime]);

  const initializeForOpen = useCallback((options: {
    preferredDate?: Dayjs;
    initialStartTime?: Dayjs;
    committed?: boolean;
  }) => {
    const normalizedPreferredDate = options.preferredDate?.startOf('day');
    setPreferredPickerValue(normalizedPreferredDate ?? options.initialStartTime);
    const nextMonth = (normalizedPreferredDate ?? options.initialStartTime ?? dayjs()).startOf('month');
    setSlotsVisibleMonth((currentMonth) => (
      currentMonth.isSame(nextMonth, 'month') ? currentMonth : nextMonth
    ));
    setHasCommittedStartTime(options.committed ?? false);
    committedInitializationRef.current = options.committed ?? false;
    setDismissedSlotUnavailable(false);
    previousIdentityKeysRef.current = undefined;
    dayPlusAutofillAppliedRef.current = false;
    pendingCalendarDayRef.current = null;
  }, [setSlotsVisibleMonth, setHasCommittedStartTime]);

  // identityKeys has a fixed length per caller, so spreading it keeps each dependency list stable.
  useEffect(() => {
    setDismissedSlotUnavailable(false);
  }, [startTime, endTime, ...identityKeys, duration]);

  useEffect(() => {
    if (!visible || !sourceId) {
      return;
    }

    const previousIdentityKeys = previousIdentityKeysRef.current;
    if (
      previousIdentityKeys !== undefined
      && previousIdentityKeys.some((key, index) => key !== identityKeys[index])
    ) {
      setHasCommittedStartTime(false);
      dayPlusAutofillAppliedRef.current = false;
      committedInitializationRef.current = false;
      if (clearStartTimeOnIdentityChange) {
        form.setFieldValue('startTime', null);
      }
    }
    previousIdentityKeysRef.current = identityKeys;

    if (committedInitializationRef.current) {
      return;
    }

    const monthSource = form.getFieldValue('startTime') ?? preferredDate ?? dayjs();
    const nextMonth = dayjs.isDayjs(monthSource) ? monthSource : dayjs(monthSource);
    if (nextMonth.isValid()) {
      const normalizedMonth = nextMonth.startOf('month');
      setSlotsVisibleMonth((currentMonth) => (
        currentMonth.isSame(normalizedMonth, 'month') ? currentMonth : normalizedMonth
      ));
    }
  }, [visible, ...identityKeys, form, preferredDate]);

  useEffect(() => {
    if (
      !visible
      || !preferredDate
      || !sourceId
      || !isSlotsQueryReady
      || dayPlusAutofillAppliedRef.current
    ) {
      return;
    }

    if (form.getFieldValue('startTime')) {
      return;
    }

    if (isCheckingSlots || !slots) {
      return;
    }

    const firstSlot = findFirstSlotStartOnDay(preferredDate, slots, timezone, slotsByDateKey);
    dayPlusAutofillAppliedRef.current = true;
    if (!firstSlot) {
      return;
    }

    form.setFieldValue('startTime', firstSlot);
    setHasCommittedStartTime(true);
  }, [
    visible,
    preferredDate,
    sourceId,
    isSlotsQueryReady,
    isCheckingSlots,
    slots,
    slotsByDateKey,
    timezone,
    form
  ]);

  useEffect(() => {
    const pendingDay = pendingCalendarDayRef.current;
    if (!visible || !sourceId || !isSlotsQueryReady || !pendingDay || isCheckingSlots || !slots) {
      return;
    }

    applyFirstSlotForDay(pendingDay);
    pendingCalendarDayRef.current = null;
  }, [visible, sourceId, isSlotsQueryReady, isCheckingSlots, slots, applyFirstSlotForDay]);

  const isSubmitBlocked = isAppointmentSlotSubmitDisabled({
    hasCommittedStartTime,
    slotUnavailable: displayedSlotUnavailable,
    truncated,
    isCheckingSelectedMonthSlots
  });

  return {
    disabledDate: schedule.disabledDate,
    disabledTime: schedule.disabledTime,
    isCheckingSlots,
    isCheckingSelectedMonthSlots,
    isSelectedSlotValid: schedule.isSelectedSlotValid,
    slotUnavailable,
    displayedSlotUnavailable,
    truncated,
    slotAlignmentMinutes: schedule.slotAlignmentMinutes,
    hasCommittedStartTime,
    setHasCommittedStartTime,
    preferredPickerValue,
    slotsVisibleMonth,
    handleSlotsVisibleMonthChange,
    handleCalendarDaySelect,
    initializeForOpen,
    resetForClose,
    setDismissedSlotUnavailable,
    isSubmitBlocked,
    startTime,
    endTime,
    timezone,
    slotsByDateKey
  };
};
